import React, { Fragment } from "react";
import Button from "components/Form/fields/Button";
import ProductNewPaymentLinkForm from "components/ProductForm/ProductNewPaymentLinkForm";
import ProductPaymentLinkForm from "components/ProductForm/ProductPaymentLinkForm";
import ProductPaymentLinksIndex from "components/ProductForm/ProductPaymentLinksIndex";
import {
  useProductPaymentLinks,
  useProduct,
  useCtxReferences
} from "utils/selectors";
import Callout from "components/Callout";
import kebabCase from "lodash/kebabCase";
import Overlay from "components/Overlay";
import { useModelQueryId } from "utils/hooks";
import { SHORT_LINK, PRODUCT, LINK } from "utils/constants/models";
import SectionBezel from "components/SectionBezel";
import { BUTTON_CLASS_CONTEXT, BUTTON_TAGS } from "utils/constants/ui";
import { useRouter } from "next/router";
import { usePrevPathFragment } from "utils/hooks/hub";
import { isModelCreateRoute } from "utils/route";

const DEFAULT_CLASSES = {
  container: "flex flex-column w-100 ph3 pv3",
  actionsWrapper: "flex flex-row justify-end items-center"
};

const ProductLinksSection = ({ customClasses }) => {
  const { resource, resourceUUID, merchantAccount } = useCtxReferences();
  const classes = { ...DEFAULT_CLASSES, ...customClasses };

  const { asPath, replace } = useRouter();
  const isCreateRoute = isModelCreateRoute(LINK, asPath);
  const createLinkPath = `${asPath}/links/create`;

  const productId = useModelQueryId(PRODUCT);
  const product = useProduct(productId);
  const shortLinkId = useModelQueryId(kebabCase(SHORT_LINK));

  const productPaymentLinks = useProductPaymentLinks(productId);

  const backPath = usePrevPathFragment(2);

  const activePaymentLinkModel = productPaymentLinks.find(
    (paymentLink) => paymentLink.uuid === shortLinkId
  );

  let content = null;

  const backToIndex = () => {
    replace(backPath);
  };

  const showOverlay = isCreateRoute || activePaymentLinkModel;

  if (activePaymentLinkModel) {
    const paymentLinkFormProps = {
      product,
      paymentLink: activePaymentLinkModel,
      goBack: backToIndex,
      resource,
      resourceUUID,
      merchantAccountUUID: merchantAccount && merchantAccount.uuid
    };
    content = <ProductPaymentLinkForm {...paymentLinkFormProps} />;
  } else if (isCreateRoute) {
    const newPaymentLinkFormProps = {
      uid: productId,
      goBack: backToIndex,
      resource,
      resourceUUID,
      merchantAccountUUID: merchantAccount && merchantAccount.uuid
    };
    content = <ProductNewPaymentLinkForm {...newPaymentLinkFormProps} />;
  }

  const actionNode = (
    <div className={classes.actionsWrapper}>
      <Button
        theme="slim"
        href={createLinkPath}
        classes={{
          contextClass: BUTTON_CLASS_CONTEXT.SECONDARY
        }}
        tag={BUTTON_TAGS.LINK}
        copy="Create"
      />
    </div>
  );

  return (
    <Fragment>
      <SectionBezel header="Payment links" action={actionNode}>
        {productPaymentLinks.length ? (
          <ProductPaymentLinksIndex paymentLinks={productPaymentLinks} />
        ) : (
          <Callout
            copy="No payment links for this product."
            customClasses={{
              container:
                "flex ph2 ba br2 items-center b--black-10 pv2 w-100 justify-between"
            }}
          />
        )}
      </SectionBezel>
      {showOverlay && content && (
        <Overlay
          hideOverlay={() => {
            replace(backPath);
          }}
        >
          <div className="pt5">{content}</div>
        </Overlay>
      )}
    </Fragment>
  );
};

export default ProductLinksSection;
